import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, TextField, MultiSelectField, Toggle, deletedRefProps, normalizeOptions } from './fields';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import { locationOptionLabel } from '../LocationTypeAhead';
import { formatCurrency } from '../../utils/money';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { KleinschmidtOptions, getKleinschmidtOptions, searchKleinschmidtTemplates, type KsTemplateMatch } from '../../services/kleinschmidt.service';
import { searchCarriers, type CarrierRow } from '../../services/carriers.service';
import LookupSearchField from './LookupSearchField';

/**
 * Bespoke edit/create drawer for Kleinschmidt Partners (cms.kleinschmidt_partners).
 *
 * Mirrors V1 editkleinschmidt_partners: EDI Handle + ISA/GS ID identity, an SFTP
 * documents folder, Bill To / Customer FK typeaheads (+ free-text additional-lists),
 * a self-referential Invoicing Order EDI Handle select, and the inbound/outbound
 * message-type toggles. Conditional save rules (V1 cantbeempty): EDI Handle + ISA/GS
 * ID always required; has_204 → Customer + Bill To; has_210 → Invoicing Bill Tos +
 * Name; has_documents_out → Documents SFTP Folder.
 */
const TABLE = 'kleinschmidt-partners';

const s = (v: any): string => (v == null ? '' : String(v));
const yn = (v: any): 'Y' | 'N' => (s(v).toUpperCase() === 'Y' ? 'Y' : 'N');
// Y/N whose column defaults to 'Y' (migration 547): only an explicit 'N' is off, so a
// record read before the migration lands reads as 'Y' rather than silently flipping the
// restriction on.
const ynY = (v: any): 'Y' | 'N' => (s(v).toUpperCase() === 'N' ? 'N' : 'Y');
// FK recno → form string; treat 0/'' as blank. On save, blank FKs persist as '0'.
const fkSeed = (v: any): string => (v && String(v) !== '0' ? String(v) : '');
const fkOut = (v: any): string => (s(v).trim() || '0');
const FK_COLS = ['billto', 'customercode', 'carriercode'];
// addlbilltos / addlcustcodes are comma-delimited recno lists (multiselect fields).
const splitList = (v: any): string[] => s(v).split(',').map((x) => x.trim()).filter(Boolean);

const INBOUND: [string, string][] = [
  ['has_204', 'Inbound 204'], ['has_214_in', 'Inbound 214'], ['has_322', 'Inbound 322'],
  ['has_856', 'Inbound 856'], ['has_940', 'Inbound 940'],
];
const OUTBOUND: [string, string][] = [
  ['has_204_out', 'Outbound 204'], ['has_210', 'Outbound 210'], ['has_214', 'Outbound 214'],
  ['has_856_out', 'Outbound 856'], ['has_944', 'Outbound 944'], ['has_945', 'Outbound 945'],
  ['has_990', 'Outbound 990'],
];
const DOCS: [string, string][] = [
  ['has_documents_out', 'EDI Document Sending'], ['has_documents_in', 'EDI Document Receiving'],
];
const YN_KEYS = [...INBOUND, ...OUTBOUND, ...DOCS].map(([k]) => k);
// Y/N columns outside the message-type grid (migration 428). Listed so the update diff
// compares them as Y/N like the toggles above — comparing a form 'N' against a raw ''
// from a record loaded before the migration would send a pointless patch every save.
const SHAPING_YN_KEYS = ['invoicing_accessorial_needs_main', 'invoicing_applyto_suffix',
  'invoicing_send_from_nonprod'];
// Y/N columns whose DDL default is 'Y' — diffed through ynY so an unset/blank record value
// compares as 'Y' and an untouched drawer sends no patch.
const DEFAULT_Y_KEYS = ['usedefaultcharges'];
// The DDL defaults (migration 428) = what an unconfigured partner gets, which is what V1
// did for every handle it had no branch for. Used only to badge the collapsed section with
// how many settings this partner actually overrides, so a closed box isn't a black hole.
// invoicing_correction_code is absent on purpose: it travels with its trigger.
const SHAPING_DEFAULTS: Record<string, string> = {
  invoicing_accessorial_scope: 'BOTH',
  invoicing_accessorial_needs_main: 'N',
  invoicing_customercode_filter: '',
  invoicing_order_edihandle_extra: '',
  invoicing_refno_source: 'INVOICE_REF',
  invoicing_po_source: 'ORDER',
  invoicing_location_code_source: 'PARTNER_CODE',
  invoicing_invoice_number_prefix: 'NONE',
  invoicing_equipment_scheme: 'SIZE_TYPE',
  invoicing_applyto_suffix: 'N',
  invoicing_correction_trigger: 'NONE',
  invoicing_send_from_nonprod: 'N',
};

const ADDR = (o: { address?: string; city?: string; state?: string; recno: number }) => {
  const line = [titleCase(s(o.address)), titleCase(s(o.city)), s(o.state)].filter(Boolean).join(', ');
  return line ? `${line}  ·  #${o.recno}` : `#${o.recno}`;
};

const KleinschmidtPartnersEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [opts, setOpts] = useState<KleinschmidtOptions | null>(null);
  const [form, setForm] = useState<TablemaintRow>({});
  const [formDeleted, setFormDeleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [tplInput, setTplInput] = useState('');  // transient text for the template-exclusion lookup
  const [carrierInput, setCarrierInput] = useState('');       // Addl. Carriers lookup text
  const [carrierPrimaryInput, setCarrierPrimaryInput] = useState('');  // Carrier lookup text
  // recno -> display name. Seeded from form-options (which resolves only the carriers
  // partner rows already reference) and extended as the user picks new ones, so a chip
  // added this session shows its name without a refetch.
  const [carrierNames, setCarrierNames] = useState<Record<string, string>>({});
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getKleinschmidtOptions().then(setOpts).catch(() => {});
    if (!isNew) tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
  }, [isNew, recno]);

  // Seed the form from the record.
  useEffect(() => {
    setForm({
      edihandle: s(record.edihandle),
      isagsid: s(record.isagsid),
      documentfolder: s(record.documentfolder),
      billto: fkSeed(record.billto),
      addlbilltos: splitList(record.addlbilltos),
      customercode: fkSeed(record.customercode),
      addlcustcodes: splitList(record.addlcustcodes),
      carriercode: fkSeed(record.carriercode),
      addlcarriercodes: splitList(record.addlcarriercodes),
      deliveryrecnolist: splitList(record.deliveryrecnolist),
      invbilltolist: splitList(record.invbilltolist),
      invbilltoname: s(record.invbilltoname),
      invorderedihandle: s(record.invorderedihandle),
      // Invoicing (210) configuration (migration 385).
      invoicing_batch_count: s(record.invoicing_batch_count),
      invoicing_template_exclusion: splitList(record.invoicing_template_exclusion),
      // Default charge-code restriction (migration 547).
      usedefaultcharges: ynY(record.usedefaultcharges),
      // Invoice file shaping (migration 428). Every one is NOT NULL with a default in the
      // DDL, so a blank on a new record seeds to that same default rather than ''.
      invoicing_accessorial_scope: s(record.invoicing_accessorial_scope) || 'BOTH',
      invoicing_accessorial_needs_main: yn(record.invoicing_accessorial_needs_main),
      invoicing_customercode_filter: s(record.invoicing_customercode_filter),
      invoicing_order_edihandle_extra: s(record.invoicing_order_edihandle_extra),
      invoicing_refno_source: s(record.invoicing_refno_source) || 'INVOICE_REF',
      invoicing_po_source: s(record.invoicing_po_source) || 'ORDER',
      invoicing_location_code_source: s(record.invoicing_location_code_source) || 'PARTNER_CODE',
      invoicing_invoice_number_prefix: s(record.invoicing_invoice_number_prefix) || 'NONE',
      invoicing_equipment_scheme: s(record.invoicing_equipment_scheme) || 'SIZE_TYPE',
      invoicing_applyto_suffix: yn(record.invoicing_applyto_suffix),
      invoicing_correction_trigger: s(record.invoicing_correction_trigger) || 'NONE',
      invoicing_correction_code: s(record.invoicing_correction_code),
      invoicing_send_from_nonprod: yn(record.invoicing_send_from_nonprod),
      ...Object.fromEntries(YN_KEYS.map((k) => [k, yn(record[k])])),
      // EDI-processing / template-matching config (migration 377). Scalars default when
      // blank (new records); the list fields split to arrays like the other multiselects.
      auto_accept: yn(record.auto_accept),
      auto_attach: yn(record.auto_attach),
      template_ignore_movetype: yn(record.template_ignore_movetype),
      // Auto-attach field requirements (migration 388).
      require_container: yn(record.require_container),
      require_po: yn(record.require_po),
      require_bol: yn(record.require_bol),
      accept_reject_time_limit: s(record.accept_reject_time_limit),
      invoice_number_format: s(record.invoice_number_format) || 'INVOICE',
      template_billto_match: s(record.template_billto_match) || 'BILLTO',
      dupe_match_fields: (() => {
        const l = splitList(record.dupe_match_fields);
        return l.length ? l : ['billto', 'movetype', 'container', 'bol']; // required ≥1
      })(),
      attach_update_fields: splitList(record.attach_update_fields),
      template_ignore_location: splitList(record.template_ignore_location),
    });
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));
  const setYN = (k: string, checked: boolean) => setField(k, checked ? 'Y' : 'N');
  const arr = (k: string): string[] => (Array.isArray(form[k]) ? (form[k] as string[]) : []);

  // Enriched typeahead option lists.
  const billtoOpts = useMemo(() => (opts?.billtos ?? []).map((b) => ({
    value: String(b.recno), label: titleCase(s(b.cname)),
    description: `${ADDR(b)}  ·  Sage ${s(b.mas90code) || '—'}  ·  AR ${formatCurrency(b.arbalance)}`,
  })), [opts]);
  const customerOpts = useMemo(() => (opts?.customers ?? []).map((c) => ({
    value: String(c.recno), label: titleCase(s(c.custname)),
    description: `${ADDR(c)}  ·  ${s(c.custtype)}`,
  })), [opts]);
  const invHandleOpts = useMemo(() => normalizeOptions(opts?.invorderedihandles ?? []), [opts]);
  useEffect(() => {
    const seeded: Record<string, string> = {};
    for (const c of opts?.carriers ?? []) seeded[String(c.recno)] = s(c.name);
    setCarrierNames((prev) => ({ ...seeded, ...prev }));  // keep names picked this session
  }, [opts]);
  const carrierLabel = useCallback(
    (recno: string) => carrierNames[recno] ? `${titleCase(carrierNames[recno])} · #${recno}` : `#${recno}`,
    [carrierNames],
  );
  // The primary Carrier input mirrors the stored recno, in the SAME `Name · #recno`
  // form as the Addl. Carriers chips — the recno is what actually gets written to
  // kleinschmidt_partners.carriercode and what has to be cross-checked against an
  // order, so showing only the name hides the value that matters.
  //
  // Seeded unconditionally rather than only-when-a-name-is-known: `carrierLabel`
  // already degrades to a bare #recno, and the earlier name-gated version left the
  // field looking EMPTY on a partner that has a carrier whenever the name could not
  // be resolved (the highway schema is unavailable in local dev).
  useEffect(() => {
    const rec = fkSeed(form.carriercode);
    setCarrierPrimaryInput(rec ? carrierLabel(rec) : '');
  }, [form.carriercode, carrierLabel]);
  const rememberCarrier = useCallback((c: CarrierRow) => {
    setCarrierNames((prev) => ({ ...prev, [String(c.recno)]: c.name }));
  }, []);
  // Recno-suffixed option lists for the Addl. Bill Tos / Customers multiselects — the
  // stored values are recnos, so surface the recno for cross-checking (same as Event Sets).
  const billtoMultiOpts = useMemo(() => (opts?.billtos ?? []).map((b) => ({
    value: String(b.recno), label: `${titleCase(s(b.cname))} · #${b.recno}`,
  })), [opts]);
  const customerMultiOpts = useMemo(() => (opts?.customers ?? []).map((c) => ({
    value: String(c.recno), label: `${titleCase(s(c.custname))} · #${c.recno}`,
  })), [opts]);
  const locationMultiOpts = useMemo(() => (opts?.locations ?? []).map((l) => ({
    value: String(l.recno), label: `${locationOptionLabel(titleCase(s(l.lname)), s(l.cyalias))} · #${l.recno}`,
  })), [opts]);
  // Static EDI-config option domains (served by /kleinschmidt/form-options).
  const movetypeOpts = useMemo(() => opts?.movetypes ?? [], [opts]);
  const invoiceFormatOpts = useMemo(() => opts?.invoiceNumberFormats ?? [], [opts]);
  const billtoMatchOpts = useMemo(() => opts?.templateBilltoMatches ?? [], [opts]);
  const dupeFieldOpts = useMemo(() => opts?.dupeMatchFields ?? [], [opts]);
  const attachFieldOpts = useMemo(() => opts?.attachUpdateFields ?? [], [opts]);
  // Invoice file shaping (migration 428).
  const accScopeOpts = useMemo(() => opts?.invoicingAccessorialScopes ?? [], [opts]);
  const refnoSrcOpts = useMemo(() => opts?.invoicingRefnoSources ?? [], [opts]);
  const poSrcOpts = useMemo(() => opts?.invoicingPoSources ?? [], [opts]);
  const locCodeSrcOpts = useMemo(() => opts?.invoicingLocationCodeSources ?? [], [opts]);
  const invNumPrefixOpts = useMemo(() => opts?.invoicingInvoiceNumberPrefixes ?? [], [opts]);
  const equipSchemeOpts = useMemo(() => opts?.invoicingEquipmentSchemes ?? [], [opts]);
  const correctionTrigOpts = useMemo(() => opts?.invoicingCorrectionTriggers ?? [], [opts]);
  const isProduction = opts?.isProduction !== false;   // assume prod until told otherwise
  const [shapingOpen, setShapingOpen] = useState(false);
  const shapingOverrideCount = useMemo(
    () => Object.entries(SHAPING_DEFAULTS).filter(([k, d]) => s(form[k]) !== d).length,
    [form],
  );

  // The EDI-processing / template-matching config only applies to inbound-order partners
  // (204 / 856 / 940) — so it's shown, and its "required" rules enforced, only for those.
  const showEdiConfig = s(form.has_204) === 'Y' || s(form.has_856) === 'Y' || s(form.has_940) === 'Y';

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (!s(form.edihandle).trim()) errs.push('EDI Handle is required.');
    if (!s(form.isagsid).trim()) errs.push('ISA/GS ID is required.');
    if (s(form.has_204) === 'Y') {
      if (!fkSeed(form.customercode)) errs.push('Customer is required when Inbound 204 is enabled.');
      if (!fkSeed(form.billto)) errs.push('Bill To is required when Inbound 204 is enabled.');
    }
    if (s(form.has_210) === 'Y') {
      if (arr('invbilltolist').length === 0) errs.push('Invoicing Bill Tos is required when Outbound 210 is enabled.');
      if (!s(form.invbilltoname).trim()) errs.push('Invoicing Bill To Name is required when Outbound 210 is enabled.');
    }
    if (s(form.has_documents_out) === 'Y' && !s(form.documentfolder).trim()) {
      errs.push('Documents SFTP Folder is required when EDI Document Sending is enabled.');
    }
    // Outbound 204 (migration 480). A partner with no delivery location AND no carrier
    // satisfies every match arm, so it would be tendered every order in the system.
    if (s(form.has_204_out) === 'Y'
        && arr('deliveryrecnolist').length === 0
        && !fkSeed(form.carriercode)
        && arr('addlcarriercodes').length === 0) {
      errs.push('Outbound 204 needs at least one Delivery Location or Carrier — '
        + 'without one, every order would be tendered to this partner.');
    }
    // EDI-config (migration 377) — required only for inbound-order partners.
    if (showEdiConfig) {
      if (!s(form.invoice_number_format).trim()) errs.push('Invoice Number Format is required.');
      if (arr('dupe_match_fields').length === 0) errs.push('At least one Dupe Matching Field is required.');
    }
    const trl = s(form.accept_reject_time_limit).trim();
    if (trl !== '' && !/^\d+$/.test(trl)) errs.push('Accept/Reject Time Limit must be a whole number of minutes (or blank for no timer).');
    const bc = s(form.invoicing_batch_count).trim();
    if (bc !== '' && (!/^\d+$/.test(bc) || parseInt(bc, 10) <= 0)) errs.push('Batch Count must be blank or a whole number greater than 0.');
    return errs;
  }, [form, showEdiConfig]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    const editable: TablemaintRow = {
      edihandle: s(form.edihandle).trim(),
      isagsid: s(form.isagsid).trim(),
      documentfolder: s(form.documentfolder),
      billto: fkOut(form.billto),
      addlbilltos: arr('addlbilltos').join(','),
      customercode: fkOut(form.customercode),
      addlcustcodes: arr('addlcustcodes').join(','),
      carriercode: fkOut(form.carriercode),
      addlcarriercodes: arr('addlcarriercodes').join(','),
      deliveryrecnolist: arr('deliveryrecnolist').join(','),
      invbilltolist: arr('invbilltolist').join(','),
      invbilltoname: s(form.invbilltoname),
      invorderedihandle: s(form.invorderedihandle),
      // Invoicing (210) config (migration 385): blank batch count → NULL (no batching).
      invoicing_batch_count: s(form.invoicing_batch_count).trim() === '' ? null : s(form.invoicing_batch_count).trim(),
      invoicing_template_exclusion: arr('invoicing_template_exclusion').join(','),
      // Default charge-code restriction (migration 547).
      usedefaultcharges: ynY(form.usedefaultcharges),
      // Invoice file shaping (migration 428).
      invoicing_accessorial_scope: s(form.invoicing_accessorial_scope),
      invoicing_accessorial_needs_main: yn(form.invoicing_accessorial_needs_main),
      invoicing_customercode_filter: s(form.invoicing_customercode_filter).trim(),
      invoicing_order_edihandle_extra: s(form.invoicing_order_edihandle_extra).trim(),
      invoicing_refno_source: s(form.invoicing_refno_source),
      invoicing_po_source: s(form.invoicing_po_source),
      invoicing_location_code_source: s(form.invoicing_location_code_source),
      invoicing_invoice_number_prefix: s(form.invoicing_invoice_number_prefix),
      invoicing_equipment_scheme: s(form.invoicing_equipment_scheme),
      invoicing_applyto_suffix: yn(form.invoicing_applyto_suffix),
      invoicing_correction_trigger: s(form.invoicing_correction_trigger),
      // Cleared alongside its trigger — the API rejects one without the other.
      invoicing_correction_code: s(form.invoicing_correction_trigger) === 'NONE'
        ? '' : s(form.invoicing_correction_code).trim().toUpperCase(),
      invoicing_send_from_nonprod: yn(form.invoicing_send_from_nonprod),
      ...Object.fromEntries(YN_KEYS.map((k) => [k, yn(form[k])])),
      // EDI-processing / template-matching config (migration 377).
      auto_accept: yn(form.auto_accept),
      auto_attach: yn(form.auto_attach),
      template_ignore_movetype: yn(form.template_ignore_movetype),
      require_container: yn(form.require_container),
      require_po: yn(form.require_po),
      require_bol: yn(form.require_bol),
      // Nullable int: blank → NULL (no timer), never 0.
      accept_reject_time_limit: s(form.accept_reject_time_limit).trim() === '' ? null : s(form.accept_reject_time_limit).trim(),
      invoice_number_format: s(form.invoice_number_format),
      template_billto_match: s(form.template_billto_match),
      dupe_match_fields: arr('dupe_match_fields').join(','),
      attach_update_fields: arr('attach_update_fields').join(','),
      template_ignore_location: arr('template_ignore_location').join(','),
    };
    try {
      if (isNew) {
        await tablemaintService.create(TABLE, editable);
      } else {
        const patch: TablemaintRow = {};
        Object.entries(editable).forEach(([k, v]) => {
          const cur = FK_COLS.includes(k) ? fkOut(record[k])
            : DEFAULT_Y_KEYS.includes(k) ? ynY(record[k])
            : (YN_KEYS.includes(k) || SHAPING_YN_KEYS.includes(k)) ? yn(record[k])
            : s(record[k]);
          if (v !== cur) patch[k] = v;
        });
        if (formDeleted !== (s(record.deleted) === 'Y')) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save Kleinschmidt partner.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, isNew, form, record, formDeleted, recno, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Partner' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  // Users know a partner by its EDI handle, not its recno; the recno is only the
  // fallback for a row whose handle is somehow blank. `record` is seeded from the
  // grid row, so the handle is in the title from the first paint.
  const ident = s(record.edihandle).trim() || `#${recno}`;
  const title = isNew ? 'Add Kleinschmidt Partner' : `Edit Kleinschmidt Partner (${ident})`;
  const invalid = (blank: boolean) => showErrors && blank;

  const toggleGrid = (items: [string, string][]) => (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
      {items.map(([k, label]) => (
        <Toggle key={k} label={label} checked={s(form[k]) === 'Y'} disabled={isSaving}
          onChange={(v) => setYN(k, v)} />
      ))}
    </div>
  );

  // A multiselect target + a visible chip list of the current selections (labels resolved
  // from the options; orphaned recnos show as #recno so nothing silently disappears).
  const renderMulti = (key: string, label: string, options: { value: string; label: string }[], placeholder: string, required = false) => {
    const values = arr(key);
    const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? `#${v}`;
    return (
      <div>
        <MultiSelectField label={label} required={required} value={values} options={options} disabled={isSaving}
          placeholder={placeholder} onChange={(v) => setField(key, v)} />
        {values.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {values.map((v) => (
              <span key={v} className="inline-flex items-center gap-1 rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300">
                {labelFor(v)}
                {!isSaving && (
                  <button type="button" onClick={() => setField(key, values.filter((x) => x !== v))}
                    className="hover:text-blue-900 dark:hover:text-blue-100">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Addl. Carriers: the same chip UX as the multiselects above, but server-searched.
  // highway.carrierprofile has ~24k ACTIVE rows — an order of magnitude past what the
  // preloaded option lists (billtos/customers/locations) can carry — so this is backed
  // by /cms-lookups/carrier-search. Chips store the recno and display the name from
  // `carrierNames`, falling back to #recno when a name could not be resolved (the
  // highway schema is unavailable in local dev).
  const renderAddlCarriers = () => {
    const values = arr('addlcarriercodes');
    const add = (c: CarrierRow) => {
      const v = String(c.recno);
      rememberCarrier(c);
      if (v && v !== '0' && !values.includes(v)) setField('addlcarriercodes', [...values, v]);
      setCarrierInput('');
    };
    return (
      <div>
        <LookupSearchField<CarrierRow>
          label="Addl. Carriers"
          displayValue={carrierInput}
          onType={setCarrierInput}
          onSelect={add}
          search={searchCarriers}
          itemValue={(c) => String(c.recno)}
          itemLabel={(c) => c.name}
          renderItem={(c) => (
            <div className="flex flex-col">
              <span className="font-medium">{titleCase(c.name)}</span>
              <span className="text-xs text-muted truncate">
                {[c.scac, [c.city, c.state].filter(Boolean).join(', '),
                  c.mcnum ? `MC ${c.mcnum}` : ''].filter(Boolean).join(' · ')}
              </span>
            </div>
          )}
          disabled={isSaving}
          placeholder="Type a carrier name…"
          clearOnBlurWithoutSelect
        />
        {values.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {values.map((v) => (
              <span key={v} className="inline-flex items-center gap-1 rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300">
                {carrierLabel(v)}
                {!isSaving && (
                  <button type="button" onClick={() => setField('addlcarriercodes', values.filter((x) => x !== v))}
                    className="hover:text-blue-900 dark:hover:text-blue-100">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Exclude Template(s): a server-searched type-ahead (14k+ templates, too many to preload)
  // that adds each picked template pronumber as a chip — the same chip UX as the multiselects,
  // but backed by /kleinschmidt/template-lookup instead of a fixed option list.
  const renderTemplateExclusion = () => {
    const values = arr('invoicing_template_exclusion');
    const add = (tpl: string) => {
      const v = (tpl || '').trim().toUpperCase();
      if (v && !values.includes(v)) setField('invoicing_template_exclusion', [...values, v]);
      setTplInput('');
    };
    return (
      <div>
        <LookupSearchField<KsTemplateMatch>
          label="Exclude Template(s)"
          displayValue={tplInput}
          onType={setTplInput}
          onSelect={(t) => add(t.template)}
          search={searchKleinschmidtTemplates}
          itemValue={(t) => t.template}
          itemLabel={(t) => t.template}
          renderItem={(t) => (
            <div className="flex flex-col">
              <span className="font-medium">{t.template}</span>
              <span className="text-xs text-muted truncate">
                {[t.type, t.customer, t.destination].filter(Boolean).join(' · ')}
              </span>
            </div>
          )}
          disabled={isSaving}
          placeholder="Type a template # or destination…"
          clearOnBlurWithoutSelect
        />
        {values.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {values.map((v) => (
              <span key={v} className="inline-flex items-center gap-1 rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300">
                {v}
                {!isSaving && (
                  <button type="button" onClick={() => setField('invoicing_template_exclusion', values.filter((x) => x !== v))}
                    className="hover:text-blue-900 dark:hover:text-blue-100">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="62rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Identity */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Identity</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="EDI Handle" required value={s(form.edihandle)} disabled={isSaving}
              onChange={(v) => setField('edihandle', v)} invalid={invalid(!s(form.edihandle).trim())} />
            <TextField label="ISA/GS ID" required value={s(form.isagsid)} disabled={isSaving}
              onChange={(v) => setField('isagsid', v)} invalid={invalid(!s(form.isagsid).trim())} />
            <TextField label="Documents SFTP Folder" value={s(form.documentfolder)} disabled={isSaving}
              onChange={(v) => setField('documentfolder', v)}
              invalid={invalid(s(form.has_documents_out) === 'Y' && !s(form.documentfolder).trim())} />
          </div>
        </fieldset>

        {/* Who this partner's events apply to */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Applies To</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="Bill To" value={s(form.billto)} onChange={(v) => setField('billto', v)}
              options={billtoOpts} disabled={isSaving} showDescriptions
              {...deletedRefProps('Bill To', record.cname, form.billto, record.billtodeleted)} />
            <SelectField label="Customer" value={s(form.customercode)} onChange={(v) => setField('customercode', v)}
              options={customerOpts} disabled={isSaving} showDescriptions
              {...deletedRefProps('Customer', record.custname, form.customercode, record.custdeleted)} />
          </div>
          <div className="mt-4 space-y-4">
            {renderMulti('addlbilltos', 'Addl. Bill Tos', billtoMultiOpts, 'Add bill tos…')}
            {renderMulti('addlcustcodes', 'Addl. Customers', customerMultiOpts, 'Add customers…')}
            {renderMulti('deliveryrecnolist', 'Delivery Locations', locationMultiOpts, 'Add delivery locations…')}
          </div>

          {/* Outbound-204 carrier targeting (migration 480). Delivery Location and
              Carrier follow V1's ksListMatch convention: blank = not a requirement,
              filled = must match, both filled = AND. At least one is required once
              Outbound 204 is on — see the validation banner. */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
            <p className="text-xs text-muted">
              Outbound 204 tendering matches on Delivery Location and/or Outside Carrier.
              Leave one blank to ignore it; fill both to require both.
            </p>
            <LookupSearchField<CarrierRow>
              label="Carrier"
              displayValue={carrierPrimaryInput}
              onType={setCarrierPrimaryInput}
              onSelect={(c) => { rememberCarrier(c); setField('carriercode', String(c.recno)); }}
              search={searchCarriers}
              itemValue={(c) => String(c.recno)}
              itemLabel={(c) => c.name}
              renderItem={(c) => (
                <div className="flex flex-col">
                  <span className="font-medium">{titleCase(c.name)}</span>
                  <span className="text-xs text-muted truncate">
                    {[c.scac, [c.city, c.state].filter(Boolean).join(', '),
                      c.mcnum ? `MC ${c.mcnum}` : ''].filter(Boolean).join(' · ')}
                  </span>
                </div>
              )}
              disabled={isSaving}
              placeholder="Type a carrier name…"
              invalid={invalid(s(form.has_204_out) === 'Y'
                && arr('deliveryrecnolist').length === 0
                && !fkSeed(form.carriercode)
                && arr('addlcarriercodes').length === 0)}
            />
            {fkSeed(form.carriercode) && !isSaving && (
              <button type="button"
                onClick={() => { setField('carriercode', ''); setCarrierPrimaryInput(''); }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                Clear carrier
              </button>
            )}
            {renderAddlCarriers()}
          </div>
        </fieldset>

        {/* Message types */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
          <legend className="px-2 text-sm font-semibold text-label">Message Types</legend>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Inbound</div>
            {toggleGrid(INBOUND)}
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Outbound</div>
            {toggleGrid(OUTBOUND)}
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Documents</div>
            {toggleGrid(DOCS)}
          </div>
        </fieldset>

        {/* Invoicing Configuration — only for outbound-invoice partners (has_210). Holds the
            batch count + template exclusions plus the Invoicing Bill Tos / Name (moved here
            from Bill To & Customer); all hidden when Outbound 210 is off. */}
        {s(form.has_210) === 'Y' && (
          <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
            <legend className="px-2 text-sm font-semibold text-label">Invoicing Configuration</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="Invoicing Order EDI Handle" value={s(form.invorderedihandle)} disabled={isSaving}
                onChange={(v) => setField('invorderedihandle', v)} options={invHandleOpts} />
              <TextField label="Batch Count" type="number" value={s(form.invoicing_batch_count)} disabled={isSaving}
                placeholder="Blank = no batching" onChange={(v) => setField('invoicing_batch_count', v)}
                invalid={invalid((() => { const bc = s(form.invoicing_batch_count).trim(); return bc !== '' && (!/^\d+$/.test(bc) || parseInt(bc, 10) <= 0); })())} />
              <TextField label="Invoicing Bill To Name" required value={s(form.invbilltoname)} disabled={isSaving}
                onChange={(v) => setField('invbilltoname', v)} invalid={invalid(!s(form.invbilltoname).trim())} />
            </div>
            {renderMulti('invbilltolist', 'Invoicing Bill Tos', billtoMultiOpts, 'Add invoicing bill tos…', true)}
            {renderTemplateExclusion()}

            {/* Default charge-code restriction (migration 547). cms.edi_chargecodemap is an
                overlay: a line item this handle has not mapped still goes out under the
                DEFAULT code. Turning this off removes that fallback, so anything unmapped
                fails validation ("Invalid line item type") and stays out of the file —
                which is what a partner who only accepts codes they agreed to needs. */}
            <Toggle label="Use Default Charge Codes"
              checked={s(form.usedefaultcharges) !== 'N'} disabled={isSaving}
              onChange={(v) => setField('usedefaultcharges', v ? 'Y' : 'N')}
              hint="Off = invoice only under charge codes mapped to this EDI handle; an unmapped line item blocks the invoice" />

            {/* Invoice File Shaping (migration 428) — how the 210 file is BUILT for this
                partner, as opposed to which invoices go in it. Collapsed by default: the
                defaults are right for 20 of the 50 live partners and these are set once
                at onboarding, so they would otherwise bury the fields that get edited.
                The badge means a closed box still tells you it holds something. */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
              <button type="button" onClick={() => setShapingOpen((o) => !o)}
                aria-expanded={shapingOpen}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-label hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg">
                <ChevronRightIcon className={`h-4 w-4 shrink-0 transition-transform ${shapingOpen ? 'rotate-90' : ''}`} />
                <span>Invoice File Shaping</span>
                {shapingOverrideCount > 0 && (
                  <span className="ml-1 rounded-full bg-gray-200 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium">
                    {shapingOverrideCount} non-default
                  </span>
                )}
                <span className="ml-auto text-xs font-normal text-muted">
                  Defaults suit most partners
                </span>
              </button>
              {shapingOpen && (
                <div className="px-3 pb-3 pt-1 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SelectField label="Accessorial Scope" clearable={false} disabled={isSaving}
                      value={s(form.invoicing_accessorial_scope)} options={accScopeOpts}
                      onChange={(v) => setField('invoicing_accessorial_scope', v)} />
                    <TextField label="Customer Filter" value={s(form.invoicing_customercode_filter)}
                      disabled={isSaving} placeholder="Blank = every customer"
                      onChange={(v) => setField('invoicing_customercode_filter', v)}
                      invalid={invalid(s(form.invoicing_customercode_filter).trim() !== ''
                        && !/^\d+(\s*,\s*\d+)*$/.test(s(form.invoicing_customercode_filter).trim()))} />
                  </div>
                  <Toggle label="Accessorial Needs Main Invoice"
                    checked={s(form.invoicing_accessorial_needs_main) === 'Y'} disabled={isSaving}
                    onChange={(v) => setYN('invoicing_accessorial_needs_main', v)}
                    hint="Hold an accessorial until its main invoice is invoiced or DO NOT BILL" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SelectField label="Reference # Source" clearable={false} disabled={isSaving}
                      value={s(form.invoicing_refno_source)} options={refnoSrcOpts}
                      onChange={(v) => setField('invoicing_refno_source', v)} />
                    <SelectField label="PO Source" clearable={false} disabled={isSaving}
                      value={s(form.invoicing_po_source)} options={poSrcOpts}
                      onChange={(v) => setField('invoicing_po_source', v)} />
                    <SelectField label="Location Code Source" clearable={false} disabled={isSaving}
                      value={s(form.invoicing_location_code_source)} options={locCodeSrcOpts}
                      onChange={(v) => setField('invoicing_location_code_source', v)} />
                    <TextField label="Additional Order EDI Handle(s)"
                      value={s(form.invoicing_order_edihandle_extra)} disabled={isSaving}
                      placeholder="Blank = only the handle above"
                      onChange={(v) => setField('invoicing_order_edihandle_extra', v)}
                      invalid={invalid(s(form.invoicing_order_edihandle_extra).trim() !== ''
                        && !/^[A-Za-z0-9_]+(\s*,\s*[A-Za-z0-9_]+)*$/.test(s(form.invoicing_order_edihandle_extra).trim()))} />
                    <SelectField label="Invoice Number Prefix" clearable={false} disabled={isSaving}
                      value={s(form.invoicing_invoice_number_prefix)} options={invNumPrefixOpts}
                      onChange={(v) => setField('invoicing_invoice_number_prefix', v)} />
                    <SelectField label="Equipment Code Scheme" clearable={false} disabled={isSaving}
                      value={s(form.invoicing_equipment_scheme)} options={equipSchemeOpts}
                      onChange={(v) => setField('invoicing_equipment_scheme', v)} />
                  </div>
                  <Toggle label="Rebill Applyto Suffix"
                    checked={s(form.invoicing_applyto_suffix) === 'Y'} disabled={isSaving}
                    onChange={(v) => setYN('invoicing_applyto_suffix', v)}
                    hint="Send the rebilled invoice as applyto-accessorialnum rather than applyto" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SelectField label="Correction Trigger" clearable={false} disabled={isSaving}
                      value={s(form.invoicing_correction_trigger)} options={correctionTrigOpts}
                      onChange={(v) => setField('invoicing_correction_trigger', v)} />
                    {/* Only shown with a trigger — a code on its own never fires, and the
                        API rejects the pair, so don't offer the invalid combination. */}
                    {s(form.invoicing_correction_trigger) !== 'NONE' && (
                      <TextField label="Correction Code" required maxLength={2}
                        value={s(form.invoicing_correction_code)} disabled={isSaving}
                        placeholder="e.g. AD or CO"
                        onChange={(v) => setField('invoicing_correction_code', v.toUpperCase())}
                        invalid={invalid(!s(form.invoicing_correction_code).trim())} />
                    )}
                  </div>
                  {/* Read only outside production, so production has no honest way to show
                      it — the value there is inert either way. */}
                  {!isProduction && (
                    <Toggle label="Send From Non-Production"
                      checked={s(form.invoicing_send_from_nonprod) === 'Y'} disabled={isSaving}
                      onChange={(v) => setYN('invoicing_send_from_nonprod', v)}
                      hint="Transmit this partner's file to Kleinschmidt even from a test system" />
                  )}
                </div>
              )}
            </div>
          </fieldset>
        )}

        {/* EDI Order Processing + Template Matching — only for inbound-order partners
            (204 / 856 / 940); used by the EDI scripts + Pending EDI "Get Template" (not
            implemented here yet). Hidden entirely for non-inbound partners. */}
        {showEdiConfig && (
          <>
            <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
              <legend className="px-2 text-sm font-semibold text-label">EDI Order Processing</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                <Toggle label="Auto-Accept" checked={s(form.auto_accept) === 'Y'} disabled={isSaving}
                  onChange={(v) => setYN('auto_accept', v)} hint="Accept new EDI orders on receipt (as system)" />
                <Toggle label="Auto-Attach" checked={s(form.auto_attach) === 'Y'} disabled={isSaving}
                  onChange={(v) => setYN('auto_attach', v)} hint="Auto-attach new EDI orders to existing pros" />
              </div>
              {/* Fields that must be non-blank for the EDI script to auto-attach (migration 388). */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Required for Auto-Attach</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                  <Toggle label="Container" checked={s(form.require_container) === 'Y'} disabled={isSaving}
                    onChange={(v) => setYN('require_container', v)} hint="Container must be non-blank to auto-attach" />
                  <Toggle label="PO" checked={s(form.require_po) === 'Y'} disabled={isSaving}
                    onChange={(v) => setYN('require_po', v)} hint="PO must be non-blank to auto-attach" />
                  <Toggle label="BOL" checked={s(form.require_bol) === 'Y'} disabled={isSaving}
                    onChange={(v) => setYN('require_bol', v)} hint="BOL must be non-blank to auto-attach" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField label="Accept/Reject Time Limit (min)" type="number" value={s(form.accept_reject_time_limit)}
                  disabled={isSaving} placeholder="Blank = no timer"
                  onChange={(v) => setField('accept_reject_time_limit', v)}
                  invalid={invalid(s(form.accept_reject_time_limit).trim() !== '' && !/^\d+$/.test(s(form.accept_reject_time_limit).trim()))} />
                <SelectField label="Invoice Number Format" required clearable={false} value={s(form.invoice_number_format)}
                  disabled={isSaving} options={invoiceFormatOpts} onChange={(v) => setField('invoice_number_format', v)}
                  invalid={invalid(!s(form.invoice_number_format).trim())} />
              </div>
              {renderMulti('dupe_match_fields', 'Dupe Matching Fields', dupeFieldOpts, 'Add fields…', true)}
              {/* One shared list for manual + auto attach (same logic in 360). */}
              {renderMulti('attach_update_fields', 'Attach Updates (manual & auto)', attachFieldOpts, 'Add fields…')}
            </fieldset>

            <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
              <legend className="px-2 text-sm font-semibold text-label">Template Matching (Get Template)</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SelectField label="Bill To Match" clearable={false} value={s(form.template_billto_match)}
                  disabled={isSaving} options={billtoMatchOpts} onChange={(v) => setField('template_billto_match', v)} />
                <div className="flex items-end pb-1">
                  <Toggle label="Ignore Move Type" checked={s(form.template_ignore_movetype) === 'Y'} disabled={isSaving}
                    onChange={(v) => setYN('template_ignore_movetype', v)} hint="Match templates regardless of move type" />
                </div>
              </div>
              {renderMulti('template_ignore_location', 'Ignore Location for Move Types', movetypeOpts, 'Add move types…')}
            </fieldset>
          </>
        )}

        {/* Soft-delete (edit only). */}
        {!isNew && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <Toggle label="Deleted" checked={formDeleted} disabled={isSaving} onChange={setFormDeleted}
              hint="Soft-deletes this partner — hidden from filters but preserved in history. Uncheck and save to restore." />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default KleinschmidtPartnersEditDrawer;
